import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userStatus, setUserStatus] = useState(null); // 'pending', 'blocked', 'active', 'stopped'
  const [showPendingPopup, setShowPendingPopup] = useState(false);
  const [showBlockedPopup, setShowBlockedPopup] = useState(false);

  // Initialize auth state from cookies/localStorage and verify session
  useEffect(() => {
    const initAuth = async () => {
      try {
        console.log('🔍 AuthContext initAuth - Starting authentication initialization...');
        
        // First check if we have a valid session via cookies (preferred method)
        console.log('🔍 AuthContext initAuth - Checking for existing session via cookies...');
        try {
          const response = await fetch(`${getApiBaseUrl()}/api/auth/verify`, {
            method: 'GET',
            credentials: 'include', // This will send cookies
          });
          
          console.log('🔍 AuthContext initAuth - Cookie verification response status:', response.status);
          
          if (response.ok) {
            const data = await response.json();
            console.log('🔍 AuthContext initAuth - Valid session found via cookies:', data);
            
            // Get full user profile data
            try {
              const profileResponse = await fetch(`${getApiBaseUrl()}/api/auth/profile`, {
                method: 'GET',
                credentials: 'include',
              });
              
              if (profileResponse.ok) {
                const profileData = await profileResponse.json();
                console.log('🔍 AuthContext initAuth - Full user profile from cookies:', profileData.user);
                setUser(profileData.user);
                localStorage.setItem('user', JSON.stringify(profileData.user));
              } else {
                // Fallback to basic user data from verify endpoint
                setUser(data.user);
                localStorage.setItem('user', JSON.stringify(data.user));
              }
            } catch (error) {
              console.log('🔍 AuthContext initAuth - Error getting full profile, using basic data:', error);
              setUser(data.user);
              localStorage.setItem('user', JSON.stringify(data.user));
            }
            
            setToken('cookie-based'); // Use a placeholder for cookie-based auth
            console.log('🔍 AuthContext initAuth - User authenticated via cookies');
            console.log('🔍 AuthContext initAuth - User data:', data.user);
            console.log('🔍 AuthContext initAuth - User status:', data.userStatus);
            setLoading(false);
            return;
          } else if (response.status === 403) {
            // Handle user status issues (blocked, pending, stopped)
            const data = await response.json();
            console.log('🔍 AuthContext initAuth - User status issue:', data);
            
            // Try to get user data from localStorage for status issues
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
              const parsedUser = JSON.parse(storedUser);
              setUser(parsedUser);
              console.log('🔍 AuthContext initAuth - Using stored user data for status issue:', parsedUser);
            }
            
            if (data.userStatus === 'blocked') {
              setUserStatus('blocked');
              setShowBlockedPopup(true);
            } else if (data.userStatus === 'pending') {
              setUserStatus('pending');
              setShowPendingPopup(true);
            } else if (data.userStatus === 'stopped') {
              setUserStatus('stopped');
            }
            
            setToken('cookie-based'); // Use a placeholder for cookie-based auth
            setLoading(false);
            return;
          } else {
            console.log('🔍 AuthContext initAuth - No valid session via cookies, checking localStorage...');
          }
        } catch (error) {
          console.log('🔍 AuthContext initAuth - Cookie verification failed, checking localStorage:', error.message);
        }
        
        // Fallback to localStorage if no cookie-based session
        const storedToken = localStorage.getItem('authToken');
        const storedUser = localStorage.getItem('user');
        
        console.log('🔍 AuthContext initAuth - Stored token exists:', !!storedToken);
        console.log('🔍 AuthContext initAuth - Stored user exists:', !!storedUser);
        console.log('🔍 AuthContext initAuth - Token value:', storedToken ? storedToken.substring(0, 20) + '...' : 'null');
        console.log('🔍 AuthContext initAuth - User value:', storedUser);
        
        if (storedToken && storedUser) {
          console.log('🔍 AuthContext initAuth - Setting token and user from localStorage...');
          setToken(storedToken);
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          console.log('🔍 AuthContext initAuth - User set:', parsedUser);
          console.log('🔍 AuthContext initAuth - Token set:', storedToken.substring(0, 20) + '...');
          
          // Verify token with backend
          try {
            console.log('🔍 AuthContext initAuth - Verifying token with backend...');
            console.log('🔍 AuthContext initAuth - Backend URL:', `${getApiBaseUrl()}/api/auth/verify`);
            console.log('🔍 AuthContext initAuth - Token being sent:', storedToken.substring(0, 20) + '...');
            
            const response = await fetch(`${getApiBaseUrl()}/api/auth/verify`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${storedToken}`,
                'Content-Type': 'application/json',
              },
              credentials: 'include',
            });
            
            console.log('🔍 AuthContext initAuth - Token verification response status:', response.status);
            console.log('🔍 AuthContext initAuth - Response headers:', Object.fromEntries(response.headers.entries()));
            
            if (!response.ok) {
              const errorText = await response.text();
              console.log('🔍 AuthContext initAuth - Token verification failed, error text:', errorText);
              // Token is invalid, clear it
              localStorage.removeItem('authToken');
              localStorage.removeItem('user');
              setToken(null);
              setUser(null);
            } else {
              const responseData = await response.json();
              console.log('🔍 AuthContext initAuth - Token verification successful, response:', responseData);
              
              // Refresh user data from backend to get latest information
              try {
                console.log('🔍 AuthContext initAuth - Refreshing user data from backend...');
                const profileResponse = await fetch(`${getApiBaseUrl()}/api/auth/profile`, {
                  method: 'GET',
                  headers: {
                    'Authorization': `Bearer ${storedToken}`,
                    'Content-Type': 'application/json',
                  },
                  credentials: 'include',
                });
                
                if (profileResponse.ok) {
                  const profileData = await profileResponse.json();
                  console.log('🔍 AuthContext initAuth - Fresh user data from backend:', profileData.user);
                  setUser(profileData.user);
                  localStorage.setItem('user', JSON.stringify(profileData.user));
                } else {
                  console.log('🔍 AuthContext initAuth - Failed to refresh user data, using stored data');
                }
              } catch (error) {
                console.log('🔍 AuthContext initAuth - Error refreshing user data:', error);
              }
            }
          } catch (error) {
            console.error('🔍 AuthContext initAuth - Token verification error:', error);
            console.error('🔍 AuthContext initAuth - Error details:', {
              name: error.name,
              message: error.message,
              stack: error.stack
            });
            // Clear invalid token
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
          }
        } else {
          console.log('🔍 AuthContext initAuth - No stored token or user found');
        }
      } catch (error) {
        console.error('🔍 AuthContext initAuth - Error during initialization:', error);
        // Clear corrupted data
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        setUser(null);
        setToken(null);
      } finally {
        console.log('🔍 AuthContext initAuth - Authentication initialization complete');
        console.log('🔍 AuthContext initAuth - Final state - token:', !!token, 'user:', !!user);
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Login function
  const login = async (usernameOrEmail, password) => {
    try {
      console.log('🔍 AuthContext login - Starting login process...');
      console.log('🔍 AuthContext login - getApiBaseUrl():', getApiBaseUrl());
      console.log('🔍 AuthContext login - Username/Email:', usernameOrEmail);
      
      setError(null);
      setLoading(true);

      const loginUrl = `${getApiBaseUrl()}/api/auth/login`;
      console.log('🔍 AuthContext login - Making request to:', loginUrl);
      
      const requestBody = { email: usernameOrEmail, password };
      console.log('🔍 AuthContext login - Request body:', requestBody);

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      let response;
      try {
        response = await fetch(loginUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(requestBody),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          throw new Error('Request timed out. Please check your connection and try again.');
        } else if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('NetworkError')) {
          throw new Error('Network error. Please check your connection and try again.');
        }
        throw fetchError;
      }

      console.log('🔍 AuthContext login - Response status:', response.status);
      console.log('🔍 AuthContext login - Response ok:', response.ok);
      console.log('🔍 AuthContext login - Response headers:', Object.fromEntries(response.headers.entries()));

      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        try {
          const text = await response.text();
          if (text) {
            data = JSON.parse(text);
            console.log('🔍 AuthContext login - Response data:', data);
          } else {
            // Empty response
            throw new Error('Server returned empty response');
          }
        } catch (parseError) {
          console.error('🔍 AuthContext login - JSON parse error:', parseError);
          if (!response.ok) {
            // Server error - provide helpful message
            if (response.status === 502) {
              throw new Error('Server is temporarily unavailable. Please try again later.');
            } else if (response.status === 503) {
              throw new Error('Service is temporarily unavailable. Please try again later.');
            } else if (response.status >= 500) {
              throw new Error('Server error. Please try again later.');
            } else {
              throw new Error('Invalid response from server. Please try again.');
            }
          } else {
            throw new Error('Failed to parse server response. Please try again.');
          }
        }
      } else {
        // Non-JSON response (likely HTML error page)
        const text = await response.text();
        console.error('🔍 AuthContext login - Non-JSON response:', text.substring(0, 200));
        
        if (response.status === 502) {
          throw new Error('Server is temporarily unavailable. Please try again later.');
        } else if (response.status === 503) {
          throw new Error('Service is temporarily unavailable. Please try again later.');
        } else if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        } else {
          throw new Error('Invalid response from server. Please try again.');
        }
      }

      if (!response.ok) {
        console.error('🔍 AuthContext login - Response not ok, throwing error');
        
        // Handle specific user status errors
        if (data && data.userStatus === 'blocked') {
          setUserStatus('blocked');
          setShowBlockedPopup(true);
          throw new Error(data.message || 'Account is blocked');
        } else if (data && data.userStatus === 'pending') {
          setUserStatus('pending');
          setShowPendingPopup(true);
          throw new Error(data.message || 'Account is pending approval');
        } else if (data && data.userStatus === 'stopped') {
          setUserStatus('stopped');
          throw new Error(data.message || 'Account is inactive');
        }
        
        throw new Error(data?.message || 'Login failed');
      }

      // Store token and user data
      const authToken = data.token || data.accessToken;
      if (authToken) {
        console.log('🔍 AuthContext login - Setting token:', authToken.substring(0, 20) + '...');
        setToken(authToken);
        localStorage.setItem('authToken', authToken);
        console.log('🔍 AuthContext login - Token stored in localStorage');
      } else {
        console.warn('🔍 AuthContext login - No token in response');
      }
      
      console.log('🔍 AuthContext login - Setting user:', data.user);
      setUser(data.user);
      localStorage.setItem('user', JSON.stringify(data.user));
      console.log('🔍 AuthContext login - User stored in localStorage');
      
      // Refresh user data from backend to get latest information (including business_type)
      // This is optional - if it fails, we still have the user data from login
      try {
        console.log('🔍 AuthContext login - Refreshing user data from backend...');
        
        // Create abort controller for timeout
        const profileController = new AbortController();
        const profileTimeoutId = setTimeout(() => profileController.abort(), 5000); // 5 second timeout
        
        let profileResponse;
        try {
          profileResponse = await fetch(`${getApiBaseUrl()}/api/auth/profile`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            signal: profileController.signal,
          });
          clearTimeout(profileTimeoutId);
        } catch (fetchError) {
          clearTimeout(profileTimeoutId);
          if (fetchError.name === 'AbortError') {
            console.log('🔍 AuthContext login - Profile refresh timed out, using login response data');
          } else {
            console.log('🔍 AuthContext login - Profile refresh failed, using login response data:', fetchError.message);
          }
          // Don't throw - we already have user data from login
          return { success: true, user: data.user };
        }
        
        if (profileResponse.ok) {
          const contentType = profileResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            try {
              const profileData = await profileResponse.json();
              console.log('🔍 AuthContext login - Fresh user data from backend:', profileData.user);
              setUser(profileData.user);
              localStorage.setItem('user', JSON.stringify(profileData.user));
              console.log('🔍 AuthContext login - User data refreshed from backend');
            } catch (parseError) {
              console.log('🔍 AuthContext login - Failed to parse profile data, using login response data');
            }
          }
        } else {
          console.log('🔍 AuthContext login - Failed to refresh user data, using login response data');
        }
      } catch (error) {
        // Silently fail - we already have user data from login
        console.log('🔍 AuthContext login - Error refreshing user data (non-critical):', error.message);
      }
      
      console.log('🔍 AuthContext login - Final state after login:');
      console.log('🔍 - token state:', !!authToken);
      console.log('🔍 - user state:', !!data.user);
      
      return { success: true, user: data.user };
    } catch (error) {
      console.error('🔍 AuthContext login - Error during login:', error);
      console.error('🔍 AuthContext login - Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Signup function (alias for register)
  const signup = async (userData) => {
    return register(userData);
  };

  // Register function
  const register = async (userData) => {
    try {
      setError(null);
      setLoading(true);

      const response = await fetch(`${getApiBaseUrl()}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle validation errors from backend
        if (data.errors && Array.isArray(data.errors)) {
          return { 
            success: false, 
            error: data.message || 'Validation failed',
            errors: data.errors 
          };
        }
        throw new Error(data.message || 'Registration failed');
      }

      // Auto-login after successful registration
      console.log('🔍 AuthContext register - Registration successful, attempting auto-login...');
      
      try {
        const loginResult = await login(userData.email, userData.password);
        if (loginResult.success) {
          console.log('🔍 AuthContext register - Auto-login successful');
          return { success: true, message: data.message, user: loginResult.user };
        } else {
          console.log('🔍 AuthContext register - Auto-login failed, user needs to login manually');
          return { success: true, message: data.message + '. Please log in with your credentials.', needsLogin: true };
        }
      } catch (loginError) {
        console.error('🔍 AuthContext register - Auto-login error:', loginError);
        return { success: true, message: data.message + '. Please log in with your credentials.', needsLogin: true };
      }
    } catch (error) {
      setError(error.message);
      // If error has errors array (from validation), preserve it
      if (error.errors && Array.isArray(error.errors)) {
        return { success: false, error: error.message, errors: error.errors };
      }
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Check user status and handle blocked/pending users
  const checkUserStatus = async (response) => {
    if (!response.ok) {
      const data = await response.json();
      if (data.userStatus === 'blocked') {
        setUserStatus('blocked');
        setShowBlockedPopup(true);
        return true;
      } else if (data.userStatus === 'pending') {
        setUserStatus('pending');
        setShowPendingPopup(true);
        return true;
      }
    }
    return false;
  };

  // Handle blocked user logout
  const handleBlockedLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setUserStatus(null);
    setShowBlockedPopup(false);
    setShowPendingPopup(false);
  };

  // Logout function
  const logout = async () => {
    try {
      // Call logout endpoint to clear cookies
      if (token) {
        await fetch(`${getApiBaseUrl()}/api/auth/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage and state
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
      setUserStatus(null);
      setShowBlockedPopup(false);
      setShowPendingPopup(false);
      setError(null);
    }
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      console.log('🔍 AuthContext updateProfile debug:');
      console.log('Profile data to update:', profileData);
      console.log('Current token:', token);
      console.log('Current user:', user);
      console.log('API base URL:', getApiBaseUrl());
      console.log('Full URL:', `${getApiBaseUrl()}/api/auth/profile`);
      
      setError(null);
      setLoading(true);

      const response = await fetch(`${getApiBaseUrl()}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(profileData),
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

      const data = await response.json();
      console.log('📡 Response data:', data);

      if (!response.ok) {
        console.error('❌ HTTP error:', response.status, data);
        throw new Error(data.message || `HTTP ${response.status}: Profile update failed`);
      }

      // Update stored user data
      const updatedUser = { ...user, ...data.user };
      console.log('🔄 Updated user data:', updatedUser);
      console.log('🔄 Original user data:', user);
      console.log('🔄 Backend response user data:', data.user);
      
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      console.log('🔄 User state updated in AuthContext');

      return { success: true, user: updatedUser };
    } catch (error) {
      console.error('💥 AuthContext updateProfile error:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Change password function
  const changePassword = async (passwordData) => {
    try {
      console.log('🔍 AuthContext changePassword - Starting password change...');
      setError(null);
      // Don't set global loading for password change to avoid conflicts

      const response = await fetch(`${getApiBaseUrl()}/api/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(passwordData),
      });

      console.log('📡 Password change response status:', response.status);
      
      const data = await response.json();
      console.log('📡 Password change response data:', data);

      if (!response.ok) {
        throw new Error(data.message || 'Password change failed');
      }

      return { success: true, message: data.message || 'Password changed successfully' };
    } catch (error) {
      console.error('💥 AuthContext changePassword error:', error);
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    const result = !!user && !!token;
    console.log('🔍 isAuthenticated() called:');
    console.log('🔍 - user:', user);
    console.log('🔍 - token:', token);
    console.log('🔍 - result:', result);
    return result;
  };

  // Check if user is admin
  const isAdmin = () => {
    return user?.isAdmin || user?.is_admin || false;
  };

  // Chat saving function for localStorage
  const saveChat = (userMessage, aiResponse, businessType = 'general', language = 'en') => {
    try {
      if (user?.id) {
        const chatKey = `chatHistory_${user.id}`;
        const existingChats = localStorage.getItem(chatKey);
        const chats = existingChats ? JSON.parse(existingChats) : [];
        
        const newChat = {
          type: 'user',
          content: userMessage,
          timestamp: new Date().toISOString(),
          businessType,
          language
        };
        
        const aiChat = {
          type: 'assistant',
          content: aiResponse,
          timestamp: new Date().toISOString(),
          businessType,
          language
        };
        
        chats.push(newChat, aiChat);
        
        // Keep only last 100 messages to avoid localStorage overflow
        if (chats.length > 100) {
          chats.splice(0, chats.length - 100);
        }
        
        localStorage.setItem(chatKey, JSON.stringify(chats));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving chat:', error);
      return false;
    }
  };

  // Get chat history from localStorage
  const getChatHistory = () => {
    try {
      if (user?.id) {
        const chatKey = `chatHistory_${user.id}`;
        const storedChats = localStorage.getItem(chatKey);
        return storedChats ? JSON.parse(storedChats) : [];
      }
      return [];
    } catch (error) {
      console.error('Error getting chat history:', error);
      return [];
    }
  };

  // Get auth headers for API calls
  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  };



  const value = {
    user,
    token,
    loading,
    error,
    userStatus,
    showPendingPopup,
    showBlockedPopup,
    login,
    signup,
    register,
    logout,
    updateProfile,
    changePassword,
    checkUserStatus,
    handleBlockedLogout,
    setShowPendingPopup,
    setShowBlockedPopup,
    isAuthenticated,
    isAdmin,
    getAuthHeaders,
    setError,
    saveChat,
    getChatHistory,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 